import { FC, useState } from 'react'

import { useMediaQuery } from 'react-responsive'

import { PrimarySkeleton } from 'src/Components/Skeletons/PrimarySkeleton'
import { AdVisualPerformanceSection } from 'src/Components/AdVisualPerformanceSection'
import { FullScreenModal } from 'src/Components/FullScreenModal'
import {
  MediaCoverCell,
  StickyColumnTable,
} from 'src/Components/StickyColumnTable'
import { TableColumn } from 'src/Components/StickyColumnTable/types'
import { MAX_TABLET_WIDTH } from 'src/Styled/variables'

import { ModalContent, ModalTitle } from './CountryPerformanceSection/styles'
import { AdVisualRow, useAdVisualPerformance } from './useAdVisualPerformance'
import { AdVisualPerformanceVideoPopup } from './AdVisualPerformanceVideoPopup'

interface AdVisualPerformanceTableProps {
  campaignId: string
}

const getOnClick =
  (row: AdVisualRow, onVideoClick: (id: string, name: string) => void) =>
  (): void =>
    onVideoClick(row.id, row.name)

const buildColumns = (
  onVideoClick: (id: string, name: string) => void,
): TableColumn<AdVisualRow>[] => [
  {
    key: 'visual',
    title: 'Visual',
    align: 'left',
    fixed: 'left',
    width: 140,
    render: (row) => (
      <MediaCoverCell
        name={row.name}
        cover={row.cover}
        onClick={getOnClick(row, onVideoClick)}
      />
    ),
  },
  { key: 'spent', title: 'Spent', width: 110 },
  { key: 'impressions', title: 'Impressions', width: 120 },
  { key: 'clicks', title: 'Clicks', width: 100 },
  { key: 'ctr', title: 'CTR', width: 90 },
  { key: 'conversions', title: 'Conversions', width: 130 },
  { key: 'costPerConversion', title: 'Cost per conversion', width: 160 },
]

export const AdVisualPerformanceTable: FC<AdVisualPerformanceTableProps> = ({
  campaignId,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const isMobileOrTablet = useMediaQuery({ maxWidth: MAX_TABLET_WIDTH })

  const { rows, isLoading, isError, selectedVideo, openVideo, closeVideo } =
    useAdVisualPerformance(campaignId)

  if (isLoading) {
    return <PrimarySkeleton style={{ height: '160px', borderRadius: '16px' }} />
  }

  if (isError || (!isLoading && rows.length === 0)) {
    return null
  }

  const columns = buildColumns(openVideo)

  return (
    <>
      <AdVisualPerformanceSection
        title='Ad visual performance'
        {...(isMobileOrTablet && { onExpand: () => setIsModalOpen(true) })}
      >
        <StickyColumnTable
          columns={columns}
          data={rows}
          rowKey={(row) => row.id}
        />
      </AdVisualPerformanceSection>
      <FullScreenModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        showCloseButton
      >
        <ModalContent>
          <ModalTitle>Ad visual performance</ModalTitle>
          <StickyColumnTable
            columns={columns}
            data={rows}
            rowKey={(row) => row.id}
          />
        </ModalContent>
      </FullScreenModal>
      {selectedVideo && (
        <AdVisualPerformanceVideoPopup
          adVisualId={selectedVideo.id}
          campaignId={campaignId}
          name={selectedVideo.name}
          onClose={closeVideo}
        />
      )}
    </>
  )
}
